/**
 * Military Locations Layer
 * Data-driven layer for displaying Chinese military locations
 *
 * Uses centralized ImagePreloader / IconRegistry to ensure each icon
 * is loaded at most once per session.
 * Uses ensureGeoJsonLayer for deterministic, idempotent layer management.
 */

import mapboxgl from 'mapbox-gl';
import type { MapPopupProps } from '@/components/molecules/MapPopup';
import { ENDPOINTS } from '@/feat/api-service-layer/endpoints';
import { BASE_URL } from '@/feat/api-service-layer/fetchClient';
import { setMapPopupContent } from '@/utils/map/mapPopup';
import { removeGeoJsonLayer, waitForStyleLoaded } from '../geoJsonLayerHelper';
import { imagePreloader } from '../ImagePreloader';

export interface MilitaryLocation {
  longitude: number;
  latitude: number;
  general_category: string;
  name?: string;
  chinese_simplified?: string;
  category_use?: string;
  alternate_names_notes?: string;
  subordinate_oversight?: string;
  description_additional_info?: string;
  facilities?: string;
  tenants_units_weapon_systems?: string;
  [key: string]: unknown;
}

/**
 * Fetch military locations from API
 * @param getToken - Function to get authentication token
 * @returns Promise resolving to array of military locations
 */
export async function fetchMilitaryLocations(getToken: () => Promise<string>): Promise<MilitaryLocation[]> {
  const token = await getToken();
  const res = await fetch(`${BASE_URL}${ENDPOINTS.militaryLocations}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error(`Failed to fetch military locations: ${res.status}`);
  const data = await res.json();
  return data.locations || data.results || data.result || (Array.isArray(data) ? data : []);
}

/**
 * Build GeoJSON FeatureCollection from locations array
 */
function buildGeoJson(locations: MilitaryLocation[]): GeoJSON.FeatureCollection {
  const valid = locations.filter(
    (loc) =>
      typeof loc.longitude === 'number' &&
      typeof loc.latitude === 'number' &&
      !Number.isNaN(loc.longitude) &&
      !Number.isNaN(loc.latitude),
  );
  return {
    type: 'FeatureCollection',
    features: valid.map((loc) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [loc.longitude, loc.latitude],
      },
      properties: loc as GeoJSON.GeoJsonProperties,
    })),
  };
}

/**
 * Add Military Locations layer from pre-fetched data
 * @param map - Mapbox map instance
 * @param locations - Array of military location objects
 */
export async function addMilitaryLocationsLayer(
  map: mapboxgl.Map | null,
  locations: MilitaryLocation[],
): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn('MilitaryLocations: style not ready, skipping');
    return;
  }

  // Group locations by type
  const byType = new Map<string, MilitaryLocation[]>();
  locations.forEach((loc) => {
    const type = loc.general_category || 'Other';
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type)?.push(loc);
  });

  // If no locations, clear all known military layers (best-effort)
  if (byType.size === 0) {
    // eslint-disable-next-line no-console
    // No military location data; skipping render.
    return;
  }

  // Process each type
  for (const [type, typeLocations] of byType) {
    const sourceId = `Chinese Military Locations $ ${type}`;
    const layerId = `Chinese Military Locations $ ${type} Layer`;

    const geojson = buildGeoJson(typeLocations);
    if (geojson.features.length === 0) {
      removeGeoJsonLayer(map, layerId, sourceId);
      continue;
    }

    // Get icon ID from registry
    const iconDef = imagePreloader.resolveMilitaryIcon(type);
    const iconId = iconDef.id;

    // Ensure icon loaded
    let useIcon = false;
    try {
      const loadedId = await imagePreloader.ensureMilitaryIconLoaded(map, type);
      useIcon = !!loadedId;
    } catch {
      useIcon = false;
    }

    // Re-check style after async icon loading
    if (!map.isStyleLoaded()) {
      const ready = await waitForStyleLoaded(map);
      if (!ready) {
        console.warn(`MilitaryLocations [${type}]: style not ready, skipping`);
        continue;
      }
    }

    try {
      // Remove existing layer/source if present
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }

      // Add source
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
      });

      // Add layer with icon or circle fallback
      if (useIcon) {
        map.addLayer({
          id: layerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'icon-image': iconId,
            'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.25, 11, 0.6],
            'icon-allow-overlap': false,
            'icon-ignore-placement': false,
            visibility: 'visible',
          },
        });
        // eslint-disable-next-line no-console
        // Rendering icon-based military location features.
      } else {
        map.addLayer({
          id: layerId,
          type: 'circle',
          source: sourceId,
          paint: {
            'circle-radius': 5,
            'circle-color': '#ef4444',
            'circle-stroke-width': 0.5,
            'circle-stroke-color': '#ffffff',
          },
          layout: {
            visibility: 'visible',
          },
        });
        // eslint-disable-next-line no-console
        // Rendering circle-based military location features.
      }

      const popup = new mapboxgl.Popup({ closeButton: false, offset: 16, maxWidth: '360px' });

      map.on('mouseenter', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
        map.getCanvas().style.cursor = 'pointer';
        const props = e.features?.[0]?.properties;
        if (!props) return;

        const popupProps: MapPopupProps = {
          badge: { label: 'Military Location', variant: 'warning' },
          title: props.name || 'Unknown facility',
          subtitle: props.chinese_simplified || undefined,
          sections: [
            {
              id: 'military-details',
              fields: [{ label: 'General Category', value: props.general_category || 'Unknown' }],
            },
          ],
        };

        setMapPopupContent(popup, e.lngLat, popupProps);
        popup.addTo(map);
      });

      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
    } catch (err) {
      console.error(`MilitaryLocations [${type}]: error adding layer`, err);
    }
  }
}

/**
 * Legacy function for backward compatibility
 * Fetches military locations and adds them to the map
 * @deprecated Use fetchMilitaryLocations + addMilitaryLocationsLayer instead
 */
export function addMilitaryLocations(map: mapboxgl.Map | null, getToken: () => Promise<string>): void {
  fetchMilitaryLocations(getToken)
    .then((locations) => {
      addMilitaryLocationsLayer(map, locations);
    })
    .catch((error) => {
      console.error('Failed to load military locations:', error);
    });
}
